/**
 * Mana.
 *
 * RULE DCC-MANA-001 - Crawlers & Customization p.15; Rules for Survival p.11.
 * Maximum Mana equals the ENHANCED INTELLIGENCE SCORE at a 1:1 ratio. The
 * source states this explicitly overrides the usual abbreviation convention:
 * "Not the Stat Mod this time, but the actual value." Max Mana is updated
 * whenever the Intelligence score changes.
 */

export interface ManaPool {
  max: number;
  current: number;
}

export function maxMana(enhancedIntelligenceScore: number): number {
  if (!Number.isInteger(enhancedIntelligenceScore) || enhancedIntelligenceScore < 0) {
    throw new Error(`Intelligence must be a whole number of at least 0, received ${enhancedIntelligenceScore}`);
  }
  return enhancedIntelligenceScore;
}

export function createFullManaPool(enhancedIntelligenceScore: number): ManaPool {
  const max = maxMana(enhancedIntelligenceScore);
  return { max, current: max };
}

export type SpendManaResult =
  | { ok: true; pool: ManaPool }
  | { ok: false; reason: string };

/** RULE DCC-MANA-002 - casting a Spell spends its Mana cost (C&C p.15). */
export function spendMana(pool: ManaPool, cost: number): SpendManaResult {
  if (!Number.isInteger(cost) || cost < 0) {
    throw new Error(`Mana cost must be a whole number of at least 0, received ${cost}`);
  }
  if (cost > pool.current) {
    return { ok: false, reason: `Not enough Mana: ${pool.current} available, ${cost} needed.` };
  }
  return { ok: true, pool: { ...pool, current: pool.current - cost } };
}

/**
 * RULE DCC-MANA-003 - current Mana can never be restored above the maximum
 * (C&C p.15).
 */
export function restoreMana(pool: ManaPool, amount: number): ManaPool {
  if (!Number.isInteger(amount) || amount < 0) {
    throw new Error(`Mana restored must be a whole number of at least 0, received ${amount}`);
  }
  return { ...pool, current: Math.min(pool.max, pool.current + amount) };
}

/** Recalculates the maximum after an Intelligence change, clamping current Mana. */
export function retuneManaPool(pool: ManaPool, enhancedIntelligenceScore: number): ManaPool {
  const max = maxMana(enhancedIntelligenceScore);
  return { max, current: Math.min(pool.current, max) };
}

/**
 * RULE DCC-MANA-004 - **PROJECT RULE, NOT RAW.** Mana regained per completed
 * combat round.
 *
 * The printed regeneration rate is deliberately overridden: it is far slower
 * than the pace the source fiction runs at, and a spellcaster who cannot cast
 * is not the game this application is for. Recorded as a project rule in
 * `docs/RULES_CONFLICTS.md` and never described as printed.
 *
 *   regen = max(1, floor(effective INT Mod / 2))
 *
 * THE FLOOR IS ONE, NOT ZERO. Every crawler regains at least a point per
 * completed round; a low or negative Intelligence Modifier slows regeneration
 * but never stops it. The clamp also still does the job the old zero-floor did,
 * because `Math.floor(-1.5)` is `-2` and regeneration must never take.
 *
 * `maxMana` still caps the result, so a crawler whose Max Mana is 0 gains
 * nothing - the floor is on the RATE, not on what the pool can hold.
 *
 * The modifier is the EFFECTIVE one - gear, Buffs and Conditions included -
 * because a Buff that raises Intelligence raises what it powers.
 */
export function manaRegenPerRound(effectiveIntMod: number): number {
  return Math.max(1, Math.floor(effectiveIntMod / 2));
}
